import { JobIdSchema } from '@eigtech/restoration-types'
import { useGetJob } from '@/api'
import { MobileIconButton, MobileIconLink } from '@/components/Core'
import { JobNoteFormDrawer } from '@/components/Domains/Jobs'
import { ROUTER_PATHS } from '@/lib/constants'
import { Can } from '@/lib/permissions'
import {
  Box,
  CardAccordion,
  GridItem,
  Heading,
  SimpleGrid,
  Stack,
  useBreakpointValue,
} from '@eigtech/ui-shared-dave'
import { createFileRoute, Navigate, useChildMatches } from '@tanstack/react-router'
import { MdNoteAdd } from 'react-icons/md'
import { useState } from 'react'
import { z } from 'zod'
import { routes } from './_layout'

const ParamsSchema = z.object({
  jobId: JobIdSchema,
  selectedMonth: z.string().optional(),
})

export const Route = createFileRoute('/_auth/jobs/$jobId/_layout/')({
  component: JobsBase,
  parseParams: ParamsSchema.parse,
})

function JobsBase() {
  const { jobId } = Route.useParams()

  const isMobile = useBreakpointValue({
    base: true,
    lg: false,
  })

  const matches = useChildMatches()

  if (isMobile && !matches.length) {
    return (
      <Box display={{ base: 'block', lg: 'none' }}>
        <JobDetailLinks />
      </Box>
    )
  }

  return <Navigate params={{ jobId }} replace to={ROUTER_PATHS.jobDetailInfo.route} />
}

function JobDetailLinks() {
  const { jobId } = Route.useParams()

  const { data: job } = useGetJob(jobId)

  const [isNoteDrawerOpen, setIsNoteDrawerOpen] = useState(false)

  return (
    <>
      <Stack spacing="4">
        <CardAccordion
          defaultToClosed
          heading={
            <Heading as="h3" size="sm">
              Description
            </Heading>
          }
        >
          {job?.jobDescription || 'No job description provided.'}
        </CardAccordion>

        <SimpleGrid columns={[2, 2, 3, 4, 6, 7]} spacing={['2', '2', '4']}>
          <Can I="create" a="note">
            <GridItem>
              <MobileIconButton Icon={MdNoteAdd} onClick={() => setIsNoteDrawerOpen(true)}>
                Add A Note
              </MobileIconButton>
            </GridItem>
          </Can>

          {routes.map((route) =>
            'permissions' in route ? (
              <Can key={route.id} I={route.permissions.action} a={route.permissions.subject}>
                <GridItem>
                  <MobileIconLink Icon={route.icon} params={{ jobId }} {...route.url}>
                    {route.label}
                  </MobileIconLink>
                </GridItem>
              </Can>
            ) : (
              <GridItem key={route.id}>
                <MobileIconLink Icon={route.icon} params={{ jobId }} {...route.url}>
                  {route.label}
                </MobileIconLink>
              </GridItem>
            )
          )}
        </SimpleGrid>
      </Stack>

      {isNoteDrawerOpen && (
        <JobNoteFormDrawer
          jobId={jobId}
          open={isNoteDrawerOpen}
          onOpenChange={setIsNoteDrawerOpen}
        />
      )}
    </>
  )
}
